import * as React from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import { Form, Row, Col, Card } from '@beehive/components';
import { isBlank } from 'utils/object';
import { element } from 'utils';
import { SUPPLIER_CONTRACTS_KEYS_MAPPING } from '../utils';

import PropTypes from 'prop-types';
import messages from 'messages/CompanyManagement';

const CONTRACT_ITEM_KEYS = [
  {
    key: 'code',
  },
  {
    key: 'effective_date',
  },
  {
    key: 'upper_limit',
  },
  {
    key: 'due_date',
  },
  {
    key: 'balance',
  },
];

const CONTRACT_CITYS = [
  [
    {
      key: 'gz',
    },
    {
      key: 'hz',
    },
  ],
  [
    {
      key: 'sh',
    },
    {
      key: 'hk',
    },
  ],
];

const BASE_INFO_KEYS = [
  {
    key: 'translated_currency',
  },
  {
    key: 'translated_invoice_type',
  },
];

const ContractItem: React.SFC<any> = (props, context) => {
  const { intl } = context;
  const { translated_city, prefixCls } = props;
  const isNoData = CONTRACT_ITEM_KEYS.every((item: any) => isBlank(props[item.key]));
  const classes = classNames(`${prefixCls}-item`, { [`${prefixCls}-item-no-data`]: isNoData });
  return (
    <div className={classes}>
      <div className={`${prefixCls}-item-title`}>{translated_city}</div>
      {
        isNoData ? intl.formatMessage(messages.noData) : (
          <Form
            isLabelVertical={false}
            mode="show"
            itemColProps={{ span: 12 }}
            itemLabelColProps={{ span: 3 }}
            itemInputColProps={{ span: 9 }}
          >
            {
              CONTRACT_ITEM_KEYS.map((item: any) => {
                let value = isBlank(props[item.key]) ? '--' : props[item.key];
                if (item.key === 'upper_limit' || item.key === 'balance') {
                  value = `${value} ${intl.formatMessage(messages.unitRMB)}`;
                }
                return (
                  <Form.Item
                    label={`${intl.formatMessage(messages[item.key])}${intl.formatMessage(messages.colon)}`}
                    key={item.key}
                    className={item.key}
                  >
                    <span>{value}</span>
                  </Form.Item>
                );
              })
            }
          </Form>
        )
      }

    </div>
  );
};

ContractItem.contextTypes = {
  intl: PropTypes.object,
};

export default class SupplierContracts extends React.Component<any, any> {
  private resizeObserver: ResizeObserver | undefined;
  private thisDOM: Element | undefined;
  private contractsRef: React.RefObject<HTMLDivElement>;

  public static defaultProps = {
    prefixCls: 'contracts-info-show',
  };

  public static contextTypes = {
    intl: PropTypes.object,
  };

  constructor(props: any) {
    super(props);
    this.contractsRef = React.createRef();
  }

  public componentDidMount() {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    const thisDOM = findDOMNode(this);
    if (thisDOM instanceof Element) {
      this.thisDOM = thisDOM;
      this.resizeObserver.observe(thisDOM);
    }
  }

  public componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private handleResize = (entries: ResizeObserverEntry[], observer: ResizeObserver): void => {
    const { prefixCls } = this.props;
    if (this.thisDOM) {
      // @ts-ignore
      const parentRow = element.closest(this.thisDOM, '.beehive-row');
      if (parentRow) {
        const rowHeight = parentRow.clientHeight;
        const thisHeight = this.thisDOM.clientHeight;
        if (thisHeight && rowHeight && thisHeight !== rowHeight && this.contractsRef.current) {
          const extraHeight = thisHeight - this.contractsRef.current.clientHeight;
          const itemHeight = (Math.abs(rowHeight - extraHeight) - 24) / 2;
          this.thisDOM.querySelectorAll(`.${prefixCls}-item`).forEach((el: any) => {
            el.style.height = `${itemHeight}px`;
          });
        }
      }
    }
  }

  public render() {
    const { intl } = this.context;
    const {
      translated_currency,
      bank_info = {},
      contract_begin_at,
      contract_end_at,
      supplier_contracts = [],
      loading,
      className,
      prefixCls,
    } = this.props;

    const { translated_invoice_type } = bank_info;
    const classes = classNames(prefixCls, className);

    const baseRow = { translated_currency, translated_invoice_type, contract_begin_at, contract_end_at };
    return (
      <Card
        titleNode={intl.formatMessage(messages.contractInfo)}
        className={classes}
        loading={loading}
      >
        <Form
          mode="show"
          isLabelVertical
          itemColProps={{ span: 5 }}
          className={`${prefixCls}-base-info`}
        >
          {
            BASE_INFO_KEYS.map((item: any) => {
              const formProps = SUPPLIER_CONTRACTS_KEYS_MAPPING[item.key].showProps || {};
              return (
                <Form.Item
                  itemKey={item.key}
                  label={intl.formatMessage(messages[item.key])}
                  key={item.key}
                  {...formProps}
                >
                  <span>{isBlank(baseRow[item.key]) ? '--' : baseRow[item.key]}</span>
                </Form.Item>
              );
            })
          }
        </Form>
        <div ref={this.contractsRef}>
          {
            CONTRACT_CITYS.map((row: any, idx: number) => {
              return (
                <Row gutter={24} key={idx} className={`${prefixCls}-item-row`}>
                  {
                    row.map((item: any) => {
                      const cityProps = supplier_contracts.find((i: any) => i.city === item.key) || {};
                      return (
                        <Col key={item.key} span={12}>
                          <ContractItem
                            {...cityProps}
                            city={item.key}
                            translated_city={`
                              ${intl.formatMessage(messages[item.key])} ${intl.formatMessage(messages.contract)}
                            `}
                            key={item.key}
                            prefixCls={prefixCls}
                          />
                        </Col>
                      );
                    })
                  }
                </Row>
              );
            })
          }
        </div>
      </Card>
    );
  }
}
